import { uiText, useUiLanguage } from '../../../utils/uiTranslation';
import React, { useEffect, useMemo, useState } from 'react';
import { ANALYTICS_GRAPH_TYPES } from './graphCatalog';

const flattenValues = (series = []) => series.flatMap((entry) => entry.values || []);

const formatValue = (value, unit = '') => {
  if (unit === '%') return `${Number(value).toFixed(1)}%`;
  if (unit === 'days' || unit === 'أيام') return `${Number(value).toFixed(1)} ${unit}`;
  return `${Number(value).toFixed(1)}${unit ? ` ${unit}` : ''}`;
};

const TimeSeriesVisual = ({ model }) => {
  useUiLanguage();
  const rootDataset = model.dataset;
  const [selectedViewId, setSelectedViewId] = useState(rootDataset?.selectedViewId || rootDataset?.selectors?.[0]?.id || '');
  useEffect(() => {
    const nextDefault = rootDataset?.selectedViewId || rootDataset?.selectors?.[0]?.id || '';
    if (!nextDefault) return;
    setSelectedViewId((currentId) => {
      if (!rootDataset?.views?.[currentId] || currentId !== nextDefault) {
        return nextDefault;
      }
      return currentId;
    });
  }, [rootDataset]);
  const dataset = rootDataset?.views?.[selectedViewId] || rootDataset;
  const graphType = model.activeGraph;
  const sizeClass = model.size || 'medium';
  const periods = dataset?.periods || [];
  const series = dataset?.series || [];
  const shouldShowInlineValues = series.length === 1;
  const shouldShowSeriesEndValues = series.length > 1;

  const rankedSeries = useMemo(
    () =>
      [...series]
        .map((entry) => ({
          ...entry,
          latestValue: entry.values?.[entry.values.length - 1] || 0
        }))
        .sort((left, right) => right.latestValue - left.latestValue),
    [series]
  );

  const { pointsBySeries, maxValue } = useMemo(() => {
    const chartWidth = 360;
    const chartHeight = 160;
    const flat = flattenValues(series);
    const max = Math.max(...flat, 1);
    return {
      maxValue: max,
      pointsBySeries: series.map((entry) => ({
        ...entry,
        points: entry.values.map((value, index) => ({
          x: 20 + (index / Math.max(periods.length - 1, 1)) * (chartWidth - 40),
          y: chartHeight - ((value / max) * (chartHeight - 24)) - 12,
          value
        }))
      }))
    };
  }, [periods.length, series]);

  if (sizeClass === 'compact' && series.length) {
    return (
      <div className="analytics-visual analytics-visual-scatter-list">
        {rankedSeries.map((entry) => (
          <div key={entry.id} className="analytics-visual-scatter-list-row">
            <div className="analytics-visual-scatter-list-name">
              <span className={`analytics-visual-legend-swatch ${entry.tone || 'accent'}`} />
              <span className="analytics-visual-label">{uiText(entry.label)}</span>
            </div>
            <div className="analytics-visual-scatter-list-metrics">
              <strong>{formatValue(entry.latestValue, dataset.unit)}</strong>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (graphType === ANALYTICS_GRAPH_TYPES.BAR_OVER_TIME) {
    const primarySeries = series[0];
    return (
      <div className={`analytics-visual analytics-visual-time-bars ${sizeClass}`}>
        {rootDataset?.selectors?.length ? (
          <div className="analytics-visual-source-picker" role="tablist" aria-label={rootDataset.selectorLabel || uiText("Selector")}>
            {rootDataset.selectors.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`analytics-visual-source-pill${selectedViewId === option.id ? ' active' : ''}`}
                onClick={() => setSelectedViewId(option.id)}
              >
                {uiText(option.label)}
              </button>
            ))}
          </div>
        ) : null}
        <div className="analytics-visual-time-grid">
          {periods.map((period, index) => {
            const value = primarySeries?.values[index] || 0;
            return (
              <div key={period.key} className="analytics-visual-time-bar-col">
                <div className="analytics-visual-time-bar-wrap">
                  <div
                    className={`analytics-visual-time-bar ${primarySeries?.tone || 'accent'}`}
                    style={{ height: `${Math.max(14, (value / maxValue) * 132)}px` }}
                  />
                </div>
                <strong className="analytics-visual-sub">{formatValue(value, dataset.unit)}</strong>
                <span className="analytics-visual-axis-label">{uiText(period.label)}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (graphType === ANALYTICS_GRAPH_TYPES.STACKED_AREA) {
    const chartWidth = 360;
    const chartHeight = 160;
    const totals = periods.map((_, index) => sum(series.map((entry) => entry.values[index] || 0)));
    return (
      <div className={`analytics-visual analytics-visual-line-chart ${sizeClass}`}>
        {rootDataset?.selectors?.length ? (
          <div className="analytics-visual-source-picker" role="tablist" aria-label={rootDataset.selectorLabel || uiText("Selector")}>
            {rootDataset.selectors.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`analytics-visual-source-pill${selectedViewId === option.id ? ' active' : ''}`}
                onClick={() => setSelectedViewId(option.id)}
              >
                {uiText(option.label)}
              </button>
            ))}
          </div>
        ) : null}
        <svg viewBox="0 0 360 180" className="analytics-visual-trend-svg" role="img" aria-label={uiText("Stacked area chart")}>
          <line x1="20" y1="150" x2="340" y2="150" className="analytics-visual-axis" />
          {series.map((entry, seriesIndex) => {
            const cumulativeValues = periods.map((_, index) =>
              sum(series.slice(0, seriesIndex + 1).map((item) => item.values[index] || 0))
            );
            const linePoints = cumulativeValues
              .map((value, index) => {
                const x = 20 + (index / Math.max(periods.length - 1, 1)) * (chartWidth - 40);
                const y = chartHeight - ((value / Math.max(...totals, 1)) * (chartHeight - 24)) - 12;
                return `${x},${y}`;
              })
              .join(' ');
            const areaPath = `M 20 150 L ${linePoints.replace(/ /g, ' L ')} L 340 150 Z`;
            return <path key={entry.id} d={areaPath} className={`analytics-visual-area-fill ${entry.tone || 'accent'}`} />;
          })}
        </svg>
      </div>
    );
  }

  const lineClass = graphType === ANALYTICS_GRAPH_TYPES.AREA_CHART ? 'analytics-visual-area-fill' : 'analytics-visual-line';

  return (
    <div className={`analytics-visual analytics-visual-line-chart ${sizeClass}`}>
      {rootDataset?.selectors?.length ? (
        <div className="analytics-visual-source-picker" role="tablist" aria-label={rootDataset.selectorLabel || uiText("Selector")}>
          {rootDataset.selectors.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`analytics-visual-source-pill${selectedViewId === option.id ? ' active' : ''}`}
              onClick={() => setSelectedViewId(option.id)}
            >
              {uiText(option.label)}
            </button>
          ))}
        </div>
      ) : null}
      <svg viewBox="0 0 360 180" className="analytics-visual-trend-svg" role="img" aria-label={uiText("Time series chart")}>
        <line x1="20" y1="150" x2="340" y2="150" className="analytics-visual-axis" />
        {pointsBySeries.map((entry) => {
          const polyline = entry.points.map((point) => `${point.x},${point.y}`).join(' ');
          const areaPath = `M 20 150 L ${polyline.replace(/ /g, ' L ')} L 340 150 Z`;
          return (
            <g key={entry.id}>
              {graphType === ANALYTICS_GRAPH_TYPES.AREA_CHART ? <path d={areaPath} className={`analytics-visual-area-fill ${entry.tone || 'accent'}`} /> : null}
              <polyline points={polyline} className={`${lineClass} ${entry.tone || 'accent'}`} />
              {entry.points.map((point, index) => (
                <g key={`${entry.id}-${periods[index]?.key}`}>
                  <circle cx={point.x} cy={point.y} r="4" className={`analytics-visual-dot ${entry.tone || 'accent'}`} />
                  {shouldShowInlineValues ? (
                    <text x={point.x} y={Math.max(18, point.y - 14)} textAnchor="middle" className="analytics-visual-series-value">
                      {formatValue(point.value, dataset.unit)}
                    </text>
                  ) : null}
                </g>
              ))}
              {shouldShowSeriesEndValues && entry.points.length ? (
                <text
                  x={entry.points[entry.points.length - 1].x + 10}
                  y={Math.max(18, entry.points[entry.points.length - 1].y + 4)}
                  textAnchor="start"
                  className="analytics-visual-series-value analytics-visual-series-value-end"
                >
                  {formatValue(entry.points[entry.points.length - 1].value, dataset.unit)}
                </text>
              ) : null}
            </g>
          );
        })}
        {periods.map((period, index) => {
          const x = 20 + (index / Math.max(periods.length - 1, 1)) * 320;
          return (
            <text key={period.key} x={x} y="168" textAnchor="middle" className="analytics-visual-axis-label">
              {uiText(period.label)}
            </text>
          );
        })}
      </svg>
      {series.length > 1 ? (
        <div className="analytics-visual-legend">
          {series.map((entry) => (
            <div key={entry.id} className="analytics-visual-legend-item">
              <span className={`analytics-visual-legend-swatch ${entry.tone || 'accent'}`} />
              <span className="analytics-visual-sub">
                {uiText(entry.label)}
                {entry.values?.length ? ` · ${formatValue(entry.values[entry.values.length - 1], dataset.unit)}` : ''}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const sum = (values = []) => values.reduce((total, value) => total + value, 0);

export default TimeSeriesVisual;
